// Referral Model - Handles referral code validation and tracking
// All API calls go through this model file following project conventions

import { db } from "../../helpers/firebase";
import {
  collection,
  doc,
  setDoc,
  getDoc,
  updateDoc,
  serverTimestamp,
  query,
  where,
  getDocs,
  orderBy,
  limit,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";

const COLLECTION_NAME = "referrals";

/**
 * Validate a referral code (agent code like ALYSSA, PATRICK, etc.)
 * @param {string} code - The referral code to validate
 * @returns {Promise<{valid: boolean, referrerId?: string, displayName?: string}>}
 */
export async function validateReferralCode(code) {
  try {
    // TODO(api): implement /api/referrals/validate?code=...

    // For now, simulate agent code validation
    const agentCodes = {
      ALYSSA: { referrerId: "agent_alyssa_123", displayName: "Alyssa P." },
      PATRICK: { referrerId: "agent_patrick_456", displayName: "Patrick M." },
      AGENT25: {
        referrerId: "agent_general",
        displayName: "Clumse Agent",
      },
    };

    const normalizedCode = code?.toUpperCase().trim();
    const agentData = agentCodes[normalizedCode];

    if (agentData) {
      return {
        valid: true,
        referrerId: agentData.referrerId,
        displayName: agentData.displayName,
      };
    }

    return { valid: false };
  } catch (error) {
    console.error("Error validating referral code:", error);
    return { valid: false };
  }
}

/**
 * Attach referral code to payment intent/session
 * @param {string} intentId - Stripe payment intent ID
 * @param {string} code - Referral code
 * @returns {Promise<{ok: boolean}>}
 */
export async function attachReferralToIntent(intentId, code) {
  try {
    // Send referral attribution to backend for Stripe metadata attachment
    const auth = getAuth();
    const headers = {
      "Content-Type": "application/json",
    };
    if (auth.currentUser) {
      headers.Authorization = `Bearer ${await auth.currentUser.getIdToken()}`;
    }

    const response = await fetch("/api/referrals/attach", {
      method: "POST",
      headers,
      body: JSON.stringify({
        paymentIntentId: intentId,
        referralCode: code,
        source: "payment_intent_attachment",
      }),
    });

    if (!response.ok) {
      throw new Error(`Referral attachment failed: ${response.statusText}`);
    }

    const result = await response.json();
    console.log("✅ [REFERRAL] Attached to payment intent:", { intentId, code, result });
    return result;
  } catch (error) {
    console.error("Error attaching referral to intent:", error);
    return { ok: false };
  }
}

/**
 * Get referral summary for current user
 * @returns {Promise<{code: string, link: string, totals: {pending: number, active: number, total: number}, rewards: {freeMonths: number, cashCents: number}}>}
 */
export async function getReferralSummary() {
  try {
    // TODO(api): GET /api/referrals/summary

    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error("User not authenticated");
    }

    // Generate user-specific referral code (first 5 chars of UID)
    const userCode = user.uid.substring(0, 5).toUpperCase();
    const referralLink = `${window.location.origin}/signup?ref=${userCode}`;

    // For now, return mock data
    return {
      code: userCode,
      link: referralLink,
      totals: {
        pending: 0,
        active: 0,
        total: 0,
      },
      rewards: {
        freeMonths: 0,
        cashCents: 0,
      },
    };
  } catch (error) {
    console.error("Error getting referral summary:", error);
    return {
      code: "USER",
      link: `${window.location.origin}/signup?ref=USER`,
      totals: { pending: 0, active: 0, total: 0 },
      rewards: { freeMonths: 0, cashCents: 0 },
    };
  }
}

/**
 * Get referral invites list
 * @param {number} page - Page number (default 1)
 * @returns {Promise<Array<{emailOrName: string, status: 'invited'|'trial'|'paying', createdAt: string}>>}
 */
export async function getReferralInvites(page = 1) {
  // TODO(api): GET /api/referrals/invites?page=...

  // For now, return empty array
  return [];
}

/**
 * Request personal code update
 * @param {string} desired - Desired referral code
 * @returns {Promise<{ok: boolean, code?: string, reason?: string}>}
 */
export async function requestPersonalCodeUpdate(desired) {
  // TODO(api): POST /api/referrals/code

  // For now, return not supported
  return {
    ok: false,
    reason: "Not supported yet",
  };
}

/**
 * Store referral code in user's checkout session
 * @param {string} userId - User ID
 * @param {string} code - Referral code
 * @param {string} referrerId - Referrer ID
 * @param {string} displayName - Referrer display name
 */
export async function storeReferralInSession(
  userId,
  code,
  referrerId,
  displayName
) {
  try {
    const referralData = {
      code,
      referrerId,
      displayName,
      createdAt: serverTimestamp(),
      status: "pending",
    };

    await setDoc(doc(db, "userReferrals", userId), referralData);

    console.log("Stored referral in session:", referralData);
  } catch (error) {
    console.error("Error storing referral in session:", error);
  }
}

/**
 * Get stored referral for user
 * @param {string} userId - User ID
 * @returns {Promise<{code?: string, referrerId?: string, displayName?: string}>}
 */
export async function getStoredReferral(userId) {
  try {
    const docRef = doc(db, "userReferrals", userId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        code: data.code,
        referrerId: data.referrerId,
        displayName: data.displayName,
      };
    }

    return {};
  } catch (error) {
    console.error("Error getting stored referral:", error);
    return {};
  }
}

/**
 * Clear stored referral after successful checkout
 * @param {string} userId - User ID
 */
export async function clearStoredReferral(userId) {
  try {
    const docRef = doc(db, "userReferrals", userId);
    await updateDoc(docRef, {
      status: "completed",
      completedAt: serverTimestamp(),
    });

    console.log("Cleared stored referral for user:", userId);
  } catch (error) {
    console.error("Error clearing stored referral:", error);
  }
}
